import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaBarcode, FaTimes, FaHistory, FaBoxes, FaSearch } from 'react-icons/fa';
import styles from './styles/StockManager.module.css';
import { useAuth } from '../../context/AuthContext';
import { nombreOperativo } from '../../utils/productName';
import shared from './styles/adminShared.module.css';
import prodStyles from './styles/Produccion.module.css';
import Pagination, { EMPTY_PAGINATION, PaginationMeta, readPaginated } from './Pagination';

interface Product {
    idProductos: number;
    NombresProductos: string;
    NombreEtiqueta?: string | null;
    barcode?: string;
    CodigoBarras?: number | string;
}

type ScanFeedback = { type: 'ok' | 'err'; text: string } | null;

interface StockMovement {
    id: number;
    producto: string;
    fecha: string;
    tipo: string;
    cantidad: number;
    motivo: string;
    usuario: string;
}

type ProduccionView = 'entrada' | 'historial';

const MOTIVO_PRODUCCION = 'Producción - desde Cocina';

const Produccion: React.FC = () => {
    const { user } = useAuth();
    const [view, setView] = useState<ProduccionView>('entrada');
    const [products, setProducts] = useState<Product[]>([]);
    const [fabricaId, setFabricaId] = useState<number | null>(null);
    const [fabricaNombre, setFabricaNombre] = useState('Fábrica');

    const [productCart, setProductCart] = useState<{ product: Product; quantity: number }[]>([]);
    const [productSearch, setProductSearch] = useState('');
    const [scanFeedback, setScanFeedback] = useState<ScanFeedback>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const [history, setHistory] = useState<StockMovement[]>([]);
    const [historySearch, setHistorySearch] = useState('');
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyMeta, setHistoryMeta] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [historyPage, setHistoryPage] = useState(1);
    const [perPage, setPerPage] = useState(50);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        fetch(`${API_URL}/productos`)
            .then(r => r.json())
            .then(d => { if (Array.isArray(d)) setProducts(d); })
            .catch(console.error);

        fetch(`${API_URL}/locales`)
            .then(r => r.json())
            .then(d => {
                if (Array.isArray(d)) {
                    const fab = d.find((l: any) => l.nombre_local.toLowerCase().includes('fabrica'));
                    if (fab) {
                        setFabricaId(fab.locales_id);
                        setFabricaNombre(fab.nombre_local);
                    }
                }
            })
            .catch(console.error);
    }, []);

    const fetchHistory = async () => {
        if (!fabricaId) return;
        setHistoryLoading(true);
        try {
            // El filtro por motivo se resuelve en el backend: filtrar aca
            // dejaria paginas incompletas o vacias.
            const params = new URLSearchParams({
                id_local: String(fabricaId),
                motivo: MOTIVO_PRODUCCION,
                page: String(historyPage),
                per_page: String(perPage),
            });
            const res = await fetch(`${API_URL}/admin/stock/movements?${params}`);
            if (res.ok) {
                const { items, pagination } = readPaginated<StockMovement>(await res.json());
                setHistory(items);
                setHistoryMeta(pagination);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (view === 'historial' && fabricaId) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, fabricaId, historyPage, perPage]);

    /*
     * Lista base de la vista de entrada: se muestran todos los productos y la
     * busqueda solo los filtra, en lugar de exigir escanear para ver algo.
     */
    const visibleProducts = useMemo(() => {
        const term = productSearch.trim().toLowerCase();
        if (!term) return products;
        return products.filter(p =>
            nombreOperativo(p).toLowerCase().includes(term) ||
            p.NombresProductos.toLowerCase().includes(term) ||
            (p.barcode || '').toLowerCase().includes(term) ||
            String(p.CodigoBarras ?? '').includes(term)
        );
    }, [products, productSearch]);

    // Suma una cantidad al carrito (reutilizado por click de búsqueda y por escáner)
    const addProductToCart = (product: Product, qty: number = 1) => {
        setProductCart(prev => {
            const existing = prev.find(item => item.product.idProductos === product.idProductos);
            if (existing) {
                return prev.map(item =>
                    item.product.idProductos === product.idProductos
                        ? { ...item, quantity: item.quantity + qty }
                        : item
                );
            }
            return [...prev, { product, quantity: qty }];
        });
        setProductSearch('');
    };

    // Escaneo: el lector USB escribe el código y manda Enter. Buscamos por
    // barcode / CodigoBarras (o id). Acepta cualquiera de los dos códigos.
    const handleScan = (raw: string) => {
        const value = raw.trim();
        if (!value) return;

        const product = products.find(p =>
            (p.barcode && p.barcode.toLowerCase() === value.toLowerCase()) ||
            (p.CodigoBarras != null && String(p.CodigoBarras) === value) ||
            String(p.idProductos) === value
        );

        if (!product) {
            setScanFeedback({ type: 'err', text: `Código "${value}" no reconocido` });
            setProductSearch('');
            setTimeout(() => setScanFeedback(null), 2500);
            // Mantener el foco para seguir escaneando
            setTimeout(() => searchRef.current?.focus(), 0);
            return;
        }

        addProductToCart(product, 1);
        setScanFeedback({ type: 'ok', text: `${nombreOperativo(product)} ✓ (+1)` });
        setTimeout(() => setScanFeedback(null), 2000);
        setTimeout(() => searchRef.current?.focus(), 0);
    };

    const removeFromCart = (id: number) => {
        setProductCart(prev => prev.filter(item => item.product.idProductos !== id));
    };

    const updateCartQuantity = (id: number, qty: number) => {
        if (qty < 1) return;
        setProductCart(prev =>
            prev.map(item => item.product.idProductos === id ? { ...item, quantity: qty } : item)
        );
    };

    const handleSubmit = async () => {
        if (!fabricaId) {
            setError('No se encontró la ubicación de Fábrica. Contacte al administrador.');
            return;
        }
        if (productCart.length === 0) {
            setError('Agregue al menos un producto para continuar.');
            return;
        }
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const today = new Date().toISOString().split('T')[0];
            for (const item of productCart) {
                const res = await fetch(`${API_URL}/admin/stock/movimiento`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id_producto: item.product.idProductos,
                        tipo: 'entrada',
                        cantidad: item.quantity,
                        motivo: MOTIVO_PRODUCCION,
                        usuario: user || 'Admin',
                        fecha: today,
                        id_local: fabricaId
                    })
                });
                if (!res.ok) throw new Error('Error al registrar producto');
            }
            setSuccess(`Producción registrada: ${productCart.length} producto(s) ingresado(s) a ${fabricaNombre}`);
            setProductCart([]);
            setProductSearch('');
        } catch {
            setError('Error al registrar la producción. Intente nuevamente.');
        } finally {
            setLoading(false);
            setTimeout(() => { setError(null); setSuccess(null); }, 6000);
        }
    };

    const tabStyle = (active: boolean, accent: string = '#0277bd') => ({
        flex: 1, padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer',
        backgroundColor: active ? '#e3f2fd' : 'transparent',
        fontWeight: 'bold' as const,
        color: active ? accent : '#666'
    });

    const filteredHistory = history.filter(m =>
        historySearch === '' || m.producto.toLowerCase().includes(historySearch.toLowerCase())
    );

    return (
        <div className={styles['stock-container']}>
            <h3>Producción</h3>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
                <button onClick={() => setView('entrada')} style={tabStyle(view === 'entrada')}>
                    <FaBoxes style={{ marginRight: '5px' }} /> Entrada de Producción
                </button>
                <button onClick={() => setView('historial')} style={tabStyle(view === 'historial')}>
                    <FaHistory style={{ marginRight: '5px' }} /> Historial
                </button>
            </div>

            {error && <div className={styles['error-msg']} style={{ marginBottom: '15px' }}>{error}</div>}
            {success && <div className={styles['success-msg']} style={{ marginBottom: '15px' }}>{success}</div>}

            {view === 'entrada' && (
                <div className={styles['transfer-cart-container']}>
                    <h4>Entrada de Producción</h4>

                    <div style={{
                        display: 'flex', gap: '20px', marginBottom: '20px',
                        background: '#e8f5e9', padding: '14px 20px', borderRadius: '8px',
                        border: '1px solid #a5d6a7', alignItems: 'center'
                    }}>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Origen</div>
                            <div style={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#2e7d32' }}>Cocina</div>
                        </div>
                        <div style={{ fontSize: '1.5rem', color: '#66bb6a' }}>→</div>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Destino</div>
                            <div style={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#2e7d32' }}>{fabricaNombre}</div>
                        </div>
                    </div>

                    <div className={styles['scanner-input-wrapper']}>
                        <FaBarcode className={styles['scanner-icon']} />
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder="Escaneá un código o buscá por nombre..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleScan(productSearch);
                                }
                            }}
                            autoComplete="off"
                            autoFocus
                            className={styles['scanner-input']}
                        />
                        {productSearch.length > 1 && (
                            <div className={styles['search-dropdown-pos']}>
                                {products
                                    .filter(p => nombreOperativo(p).toLowerCase().includes(productSearch.toLowerCase()) || p.NombresProductos.toLowerCase().includes(productSearch.toLowerCase()))
                                    .slice(0, 8)
                                    .map(p => (
                                        <div
                                            key={p.idProductos}
                                            className={styles['search-item-pos']}
                                            onClick={() => addProductToCart(p, 1)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span>{nombreOperativo(p)}</span>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>

                    {scanFeedback && (
                        <div style={{
                            margin: '10px 0',
                            padding: '8px 14px',
                            borderRadius: '6px',
                            fontWeight: 'bold',
                            color: 'white',
                            backgroundColor: scanFeedback.type === 'ok' ? '#388e3c' : '#c62828'
                        }}>
                            {scanFeedback.text}
                        </div>
                    )}

                    {/*
                      * Listado completo de productos, visible desde el inicio.
                      * Antes habia que escanear o escribir para ver algo; ahora
                      * el escaner y la busqueda solo filtran esta lista.
                      */}
                    <div className={prodStyles.catalogo}>
                        <div className={prodStyles.catalogoHeader}>
                            <h4>Productos disponibles</h4>
                            <span className={prodStyles.catalogoCount}>
                                {visibleProducts.length} de {products.length}
                            </span>
                        </div>
                        {products.length === 0 ? (
                            <p className={prodStyles.catalogoEmpty}>Cargando productos...</p>
                        ) : visibleProducts.length === 0 ? (
                            <p className={prodStyles.catalogoEmpty}>
                                Ningun producto coincide con "{productSearch}".
                            </p>
                        ) : (
                            <div className={prodStyles.catalogoGrid}>
                                {visibleProducts.map(p => {
                                    const enCarrito = productCart.find(
                                        item => item.product.idProductos === p.idProductos
                                    );
                                    return (
                                        <button
                                            key={p.idProductos}
                                            type="button"
                                            className={`${prodStyles.catalogoItem} ${enCarrito ? prodStyles.catalogoItemActivo : ''}`}
                                            onClick={() => addProductToCart(p, 1)}
                                        >
                                            <span className={prodStyles.catalogoNombre}>
                                                {nombreOperativo(p)}
                                            </span>
                                            {enCarrito && (
                                                <span className={prodStyles.catalogoBadge}>
                                                    {enCarrito.quantity}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {productCart.length > 0 && (
                        <>
                            <div className={styles['cart-table-container']}>
                                <table className={styles['cart-table-pos']}>
                                    <thead>
                                        <tr>
                                            <th>Producto</th>
                                            <th style={{ textAlign: 'center' }}>Cantidad</th>
                                            <th style={{ textAlign: 'right' }}>Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productCart.map(item => (
                                            <tr key={item.product.idProductos}>
                                                <td>{nombreOperativo(item.product)}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="number"
                                                        value={item.quantity}
                                                        onChange={(e) => updateCartQuantity(item.product.idProductos, parseInt(e.target.value) || 1)}
                                                        className={styles['qty-input-mini']}
                                                        style={{ width: '70px', textAlign: 'center', margin: '0 auto' }}
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <button
                                                        onClick={() => removeFromCart(item.product.idProductos)}
                                                        className={styles['remove-btn-pos']}
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button
                                className={styles['finalize-btn-pos']}
                                onClick={handleSubmit}
                                disabled={loading}
                                style={{ backgroundColor: '#388e3c' }}
                            >
                                {loading ? 'Registrando...' : 'REGISTRAR PRODUCCIÓN'}
                            </button>
                        </>
                    )}
                </div>
            )}

            {view === 'historial' && (
                <div className={styles['history-section']}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <h4 style={{ margin: 0 }}>Historial de Producción</h4>
                        <div style={{ position: 'relative' }}>
                            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }} />
                            <input
                                type="text"
                                placeholder="Buscar producto..."
                                value={historySearch}
                                onChange={(e) => setHistorySearch(e.target.value)}
                                style={{ padding: '8px 8px 8px 32px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.9rem', minWidth: '200px' }}
                            />
                        </div>
                    </div>
                    <div className={shared.asyncRegion}>
                    {historyLoading ? (
                        <p className={shared.loadingState}>Cargando historial...</p>
                    ) : (
                        <table className={styles['stock-table']}>
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Producto</th>
                                    <th>Cantidad</th>
                                    <th>Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredHistory.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                            No hay registros de producción
                                        </td>
                                    </tr>
                                ) : (
                                    filteredHistory.map(m => (
                                        <tr key={m.id}>
                                            <td>{new Date(m.fecha).toLocaleString('es-AR')}</td>
                                            <td>{m.producto}</td>
                                            <td style={{ color: 'green', fontWeight: 'bold' }}>+{m.cantidad}</td>
                                            <td>{m.usuario}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                    </div>
                    <Pagination
                        meta={historyMeta}
                        onPageChange={setHistoryPage}
                        onPerPageChange={setPerPage}
                        itemLabel="registros"
                    />
                </div>
            )}
        </div>
    );
};

export default Produccion;
